"use client";

import type { PhoneClickPlacement } from "@homeinstead/analytics";
import type { NavActionCtaData } from "@homeinstead/shared/site-builder";
import type { NavActionCtaVariant } from "../../types";

import {
  trackPhoneClick,
  trackScrollToTopTrackedClickEvent,
} from "@homeinstead/analytics";
import Phone from "@homeinstead/icons/Phone";
import { scrollToTop } from "@homeinstead/shared";
import Image from "next/image";
import { cn } from "../../utils/classnames";
import { Button } from "../Button";
import { Link } from "../Link";
import PhoneLink from "../PhoneLink";

const DEFAULT_PLACEMENT: PhoneClickPlacement = "header";
const PHONE_NUMBER_PLACEHOLDER = "{{phoneNumber}}";

type NavActionCtaProps = {
  cta: NavActionCtaData;
  /** Office phone resolved from the route context; used to replace `{{phoneNumber}}` placeholders in both label and tel action target. */
  phoneNumber: string;
  className?: string;
  /** The element variant. Defaults to `"primary"`. */
  variant?: NavActionCtaVariant;
  /**
   * Placement context attached to phone-click analytics so the experimentation
   * owner can attribute call leads to a specific surface
   */
  placement?: PhoneClickPlacement;
};

/**
 * Replace every {{phoneNumber}} occurrence in `text` with the route's office
 * phone number. Matches the placeholder convention used in the lead form
 * builder's Phone Link CTA.
 */
function resolvePlaceholders(text: string, phoneNumber: string): string {
  return text.split(PHONE_NUMBER_PLACEHOLDER).join(phoneNumber);
}

function resolvePhoneNumber(rawValue: string, fallback: string): string {
  if (rawValue.trim() === "") {
    return fallback;
  }

  return rawValue === PHONE_NUMBER_PLACEHOLDER ? fallback : rawValue;
}

type CtaIconProps = {
  customIconUrl?: string | null;
  iconKind: NavActionCtaData["icon"];
};

function CtaIcon({ customIconUrl, iconKind }: CtaIconProps) {
  if (iconKind === "phone") {
    return <Phone className="fill-current" />;
  }

  if (iconKind === "custom" && customIconUrl) {
    return (
      <Image
        alt=""
        aria-hidden="true"
        className="h-5 w-5"
        height={20}
        src={customIconUrl}
        width={20}
      />
    );
  }

  return null;
}

type CtaContentProps = CtaIconProps & {
  label: string;
  /**
   * When non-null, this label renders on small viewports (below `md`) and
   * the default `label` renders on `md` and above. When null, the default
   * `label` renders on every viewport (single span).
   */
  labelSmallViewport: string | null;
};

function CtaContent({
  customIconUrl,
  iconKind,
  label,
  labelSmallViewport,
}: CtaContentProps) {
  return (
    <>
      <CtaIcon customIconUrl={customIconUrl} iconKind={iconKind} />
      {labelSmallViewport === null ? (
        <span>{label}</span>
      ) : (
        <>
          <span className="md:hidden">{labelSmallViewport}</span>
          <span className="hidden md:inline">{label}</span>
        </>
      )}
    </>
  );
}

/**
 * Render a navigation action CTA primitive as the appropriate semantic
 * element for its action target — Link for url/mailto, PhoneLink for tel
 * (with {{phoneNumber}} placeholder support), Button for scrollToTop.
 *
 * The renderer is the single point where the primitive's discriminated
 * action target translates into a rendered element. NavBar, NavBarCoBranded,
 * and the bottom-sticky panel all consume this component so styling, icon
 * behavior, and placeholder resolution stay consistent across placements.
 */
function NavActionCta({
  className,
  cta,
  phoneNumber,
  placement = DEFAULT_PLACEMENT,
  variant = "primary",
}: NavActionCtaProps) {
  const ctaClassName = cn("gap-2", className);

  const resolvedLabel = resolvePlaceholders(cta.label, phoneNumber);
  const resolvedLabelSmallViewport = cta.labelSmallViewport
    ? resolvePlaceholders(cta.labelSmallViewport, phoneNumber)
    : null;

  const content = (
    <CtaContent
      customIconUrl={cta.customIcon?.asset?.url ?? null}
      iconKind={cta.icon}
      label={resolvedLabel}
      labelSmallViewport={resolvedLabelSmallViewport}
    />
  );

  const target = cta.actionTarget;

  if (target.kind === "tel") {
    return (
      <PhoneLink
        className={ctaClassName}
        onClick={() => {
          trackPhoneClick({ placement });
        }}
        phoneNumber={resolvePhoneNumber(target.phoneNumber, phoneNumber)}
        variant={variant}
      >
        {content}
      </PhoneLink>
    );
  }

  if (target.kind === "url") {
    return (
      <Link className={ctaClassName} href={target.href} variant={variant}>
        {content}
      </Link>
    );
  }

  if (target.kind === "mailto") {
    return (
      <Link
        className={ctaClassName}
        href={`mailto:${target.email}`}
        variant={variant}
      >
        {content}
      </Link>
    );
  }

  if (target.kind === "scrollToTop") {
    return (
      <Button
        className={ctaClassName}
        onClick={() => {
          trackScrollToTopTrackedClickEvent();
          scrollToTop();
        }}
        variant={variant === "secondary" ? "outline" : variant}
      >
        {content}
      </Button>
    );
  }

  return null;
}

export { NavActionCta };
